import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";
import type { Guest } from "@/types/camping";

interface PrivacyPreview {
  already_erased: boolean;
  invoice_count: number;
  booking_count: number;
  erased_at: string | null;
}

interface PrivacyResult extends PrivacyPreview {
  guest: Guest;
}

export function GuestPrivacyErasureModal({
  open,
  guest,
  onClose,
  onErased,
}: {
  open: boolean;
  guest: Guest;
  onClose: () => void;
  onErased: (guest: Guest) => void;
}) {
  const [preview, setPreview] = useState<PrivacyPreview | null>(null);
  const [confirmName, setConfirmName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setConfirmName("");
    setError(null);
    setPreview(null);
    setLoadingPreview(true);
    api
      .get<PrivacyPreview>(`/api/v1/guests/${guest.id}/privacy-erasure`)
      .then(setPreview)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Vorschau konnte nicht geladen werden.");
      })
      .finally(() => setLoadingPreview(false));
  }, [open, guest.id]);

  const nameMatches =
    confirmName.trim().toLocaleLowerCase("de") === guest.last_name.trim().toLocaleLowerCase("de");
  const blocked = preview?.already_erased === true;

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.post<PrivacyResult>(`/api/v1/guests/${guest.id}/privacy-erasure`, {});
      onErased(result.guest);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Löschung fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title="Gastdaten löschen"
      description="Stammdaten werden gelöscht. Rechnungen und Zahlungen bleiben für die Aufbewahrungsfrist erhalten."
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="danger"
            loading={submitting}
            disabled={!nameMatches || blocked || loadingPreview || submitting}
            onClick={() => void submit()}
          >
            Endgültig löschen
          </Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm text-label">
        {loadingPreview ? <p className="text-label-secondary">Wird vorbereitet…</p> : null}
        {preview ? (
          <p>
            {preview.invoice_count}{" "}
            {preview.invoice_count === 1 ? "Rechnung bleibt" : "Rechnungen bleiben"} erhalten,{" "}
            {preview.booking_count}{" "}
            {preview.booking_count === 1 ? "Aufenthalt bleibt" : "Aufenthalte bleiben"} als Buchung
            erhalten.
          </p>
        ) : null}

        <div>
          <p className="font-medium">Wird gelöscht</p>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-label-secondary">
            <li>Name, Anschrift, E-Mail, Telefon, Geburtsdatum und Ausweisnummer in der Stammkartei</li>
            <li>Kennzeichen, Mitreisende und Notizen an den Aufenthalten</li>
            <li>Gastportal und Zugangscodes</li>
            <li>Kopien in Änderungsprotokoll, Serienmail und Tablet-Unterschrift</li>
          </ul>
        </div>

        <div>
          <p className="font-medium">Bleibt gespeichert und eingeschränkt</p>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-label-secondary">
            <li>Rechnung, Zahlung und das gespeicherte Rechnungs-PDF, inklusive Name und Anschrift auf dem Beleg</li>
            <li>Gesendete Schreiben in der Datenbank und im Postfach, für die Aufbewahrungsfrist</li>
          </ul>
        </div>

        <p className="text-label-secondary">
          Ein Datenbank-Backup wird nicht nachträglich verändert. Die Löschung steht in einem
          separaten Protokoll und wird nach einem Restore der Datenbank automatisch erneut
          angewendet, solange diese Protokolldatei erhalten bleibt. Sicherungskopien laufen mit dem
          Backup-Zyklus ab.
        </p>

        <label className="block">
          <span className="text-label-secondary">
            Zur Bestätigung den Nachnamen <span className="font-medium text-label">{guest.last_name}</span>{" "}
            eingeben
          </span>
          <input
            className="eh-input mt-1"
            value={confirmName}
            autoComplete="off"
            disabled={submitting || blocked}
            onChange={(event) => setConfirmName(event.target.value)}
          />
        </label>

        {blocked ? (
          <p className="text-status-orange">Dieser Gast ist bereits gelöscht.</p>
        ) : null}
        {error ? <p className="text-status-red">{error}</p> : null}
      </div>
    </Modal>
  );
}
